import React from 'react';
import { Box, Typography, Paper, Grid, Button, Stack, alpha, useTheme } from '@mui/material';
import { CloudUpload, AutoAwesome, CheckCircle, Cable } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { CONNECTOR_COLORS } from '../../types/connectorCatalog';

interface WelcomePageProps {
  onCreateProject?: () => void;
  /** When shown inside an existing project, adapt messaging */
  hasProject?: boolean;
}

/** Top connector logos to showcase (connector_id, display letter, name). */
const SHOWCASE_CONNECTORS = [
  { id: 'salesforce', letter: 'SF', name: 'Salesforce' },
  { id: 'hubspot', letter: 'HS', name: 'HubSpot' },
  { id: 'postgresql', letter: 'PG', name: 'PostgreSQL' },
  { id: 's3', letter: 'S3', name: 'Amazon S3' },
  { id: 'snowflake', letter: 'SF', name: 'Snowflake' },
  { id: 'bigquery', letter: 'BQ', name: 'BigQuery' },
  { id: 'stripe', letter: 'ST', name: 'Stripe' },
  { id: 'shopify', letter: 'SH', name: 'Shopify' },
  { id: 'mysql', letter: 'MY', name: 'MySQL' },
  { id: 'azure_blob', letter: 'AZ', name: 'Azure Blob' },
];

const WelcomePage: React.FC<WelcomePageProps> = ({ onCreateProject, hasProject = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: hasProject ? 'auto' : '60vh',
        textAlign: 'center',
        p: 4,
      }}
    >
      {!hasProject && (
        <Box
          component="img"
          src="/images/migratr.ai L.png"
          alt="migratr"
          sx={{
            height: 22,
            mb: 4,
            opacity: 0.9,
            filter: 'grayscale(0.2) contrast(1.1)',
          }}
        />
      )}
      <Typography
        variant="h3"
        gutterBottom
        fontWeight={800}
        sx={{
          background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        {hasProject ? 'Get Started' : 'Welcome to Migratr'}
      </Typography>
      <Typography
        variant="h6"
        color="text.secondary"
        sx={{ maxWidth: 560, mb: 3, fontWeight: 400, lineHeight: 1.6 }}
      >
        {hasProject
          ? 'This project has no transformations yet. Upload your data to begin.'
          : 'Transform your data from any format into the structure you need. Select a project or create a new one to get started.'}
      </Typography>

      {/* Connector Showcase Ticker */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Connect to 30+ data sources and destinations
        </Typography>
        <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
          {SHOWCASE_CONNECTORS.map(({ id, letter, name }) => {
            const color = CONNECTOR_COLORS[id] || theme.palette.primary.main;
            return (
              <Box
                key={id}
                title={name}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(color, 0.1),
                  color: color,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  border: `1px solid ${alpha(color, 0.2)}`,
                  transition: 'all 0.2s',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${alpha(color, 0.2)}`,
                  },
                }}
                onClick={() => navigate('/connectors')}
              >
                {letter}
              </Box>
            );
          })}
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            mt: 1.5,
            display: 'block',
            cursor: 'pointer',
            '&:hover': { color: 'primary.main' },
          }}
          onClick={() => navigate('/connectors')}
        >
          Databases &middot; Warehouses &middot; CRM &middot; Payments &middot; Files &middot; APIs &nbsp;&rarr;
        </Typography>
      </Box>

      <Grid container spacing={4} maxWidth={900}>
        <Grid item xs={12} md={3}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              transition: 'all 0.2s',
              cursor: 'pointer',
              '&:hover': { borderColor: 'info.main', transform: 'translateY(-2px)' },
            }}
            onClick={() => navigate('/connectors')}
          >
            <Cable color="info" sx={{ fontSize: 40 }} />
            <Typography variant="h6" fontWeight={600}>
              Connect
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pick from 30+ connectors for databases, SaaS apps, and files.
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              transition: 'all 0.2s',
              '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)' },
            }}
          >
            <CloudUpload color="primary" sx={{ fontSize: 40 }} />
            <Typography variant="h6" fontWeight={600}>
              Upload
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Drop your CSV, Excel, or JSON files and we take it from there.
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              transition: 'all 0.2s',
              '&:hover': { borderColor: 'secondary.main', transform: 'translateY(-2px)' },
            }}
          >
            <AutoAwesome color="secondary" sx={{ fontSize: 40 }} />
            <Typography variant="h6" fontWeight={600}>
              AI Mapping
            </Typography>
            <Typography variant="body2" color="text.secondary">
              AI automatically maps your fields to the target format.
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              transition: 'all 0.2s',
              '&:hover': { borderColor: 'success.main', transform: 'translateY(-2px)' },
            }}
          >
            <CheckCircle color="success" sx={{ fontSize: 40 }} />
            <Typography variant="h6" fontWeight={600}>
              Transform
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review, approve, and get your clean data ready to use.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {onCreateProject && (
        <Button
          variant="contained"
          size="large"
          startIcon={hasProject ? <CloudUpload /> : undefined}
          onClick={onCreateProject}
          sx={{ mt: 5 }}
        >
          {hasProject ? 'Start Your First Transformation' : 'Create Your First Project'}
        </Button>
      )}
    </Box>
  );
};

export default WelcomePage;
